import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Building2, 
  Play, 
  Pause, 
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';

interface CompanyStatus {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'error';
  imported: number;
  failed: number;
  currentPage: number;
  error?: string;
}

const SuccessWareFullImportPage = () => {
  const { theme } = useTheme();
  const [companies, setCompanies] = useState<CompanyStatus[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pausedRef = useRef(false);

  // Fetch companies from database on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data, error } = await supabase
          .from('company_configs')
          .select('id, name')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;

        if (data) {
          setCompanies(data.map(c => ({
            id: c.id,
            name: c.name,
            status: 'pending' as const,
            imported: 0,
            failed: 0,
            currentPage: 0
          })));
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const updateCompanyStatus = (companyId: string, updates: Partial<CompanyStatus>) => {
    setCompanies(prev => prev.map(c => 
      c.id === companyId ? { ...c, ...updates } : c
    ));
  };

  const importCompany = async (companyId: string, companyName: string) => {
    updateCompanyStatus(companyId, { status: 'running' });
    
    let page = 0;
    let hasMore = true;
    
    try {
      while (hasMore && !pausedRef.current) {
        console.log(`Importing ${companyName} - Page ${page}`);
        
        const { data, error } = await supabase.functions.invoke('successware-import', {
          body: {
            company_id: companyId,
            import_type: 'customers',
            batch_size: 100
          }
        });

        if (error) {
          throw new Error(error.message || 'Edge function error');
        }

        if (!data || !data.success) {
          throw new Error(data?.error || 'Import failed');
        }

        const currentCompany = companies.find(c => c.id === companyId);
        
        updateCompanyStatus(companyId, {
          imported: (currentCompany?.imported || 0) + (data.imported_count || 0),
          failed: (currentCompany?.failed || 0) + (data.failed_count || 0),
          currentPage: page + 1
        });

        hasMore = data.has_more || false;
        page++;

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (!pausedRef.current) {
        updateCompanyStatus(companyId, { status: 'completed' });
      } else {
        updateCompanyStatus(companyId, { status: 'paused' });
      }
    } catch (error: any) {
      console.error(`Error importing ${companyName}:`, error);
      updateCompanyStatus(companyId, { 
        status: 'error', 
        error: error.message 
      });
    }
  };

  const startImport = async () => {
    setIsImporting(true);
    pausedRef.current = false;

    for (const company of companies) {
      if (pausedRef.current) break;
      if (company.status === 'completed') continue;
      
      await importCompany(company.id, company.name);
    }

    setIsImporting(false);
  };

  const pauseImport = () => {
    pausedRef.current = true;
    setIsImporting(false);
  };

  const resetImport = () => {
    setCompanies(prev => prev.map(c => ({
      ...c,
      status: 'pending' as const,
      imported: 0,
      failed: 0,
      currentPage: 0,
      error: undefined
    })));
    setIsImporting(false);
    pausedRef.current = false;
  };

  const totalImported = companies.reduce((sum, c) => sum + c.imported, 0);
  const totalFailed = companies.reduce((sum, c) => sum + c.failed, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.bgPage }}>
        <div className="text-center">
          <Loader2 size={48} className="animate-spin mx-auto mb-4" style={{ color: theme.colors.primaryColor }} />
          <p style={{ color: theme.colors.textSecondary }}>Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.textPrimary }}>
            SuccessWare Full Import
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Import all customer data from SuccessWare for all companies
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard }}>
            <p className="text-sm mb-2" style={{ color: theme.colors.textSecondary }}>Total Imported</p>
            <p className="text-4xl font-bold" style={{ color: theme.colors.successText }}>{totalImported.toLocaleString()}</p>
          </div>
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard }}>
            <p className="text-sm mb-2" style={{ color: theme.colors.textSecondary }}>Total Failed</p>
            <p className="text-4xl font-bold" style={{ color: theme.colors.dangerText }}>{totalFailed.toLocaleString()}</p>
          </div>
        </div>

        {/* Company Cards */}
        <div className="space-y-4 mb-6">
          {companies.map(company => (
            <div key={company.id} className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building2 size={24} style={{ color: theme.colors.primaryColor }} />
                  <h3 className="text-xl font-semibold" style={{ color: theme.colors.textPrimary }}>
                    {company.name}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {company.status === 'running' && (
                    <Loader2 size={20} style={{ color: theme.colors.primaryColor }} className="animate-spin" />
                  )}
                  {company.status === 'completed' && (
                    <CheckCircle2 size={20} style={{ color: theme.colors.successText }} />
                  )}
                  {company.status === 'error' && (
                    <AlertCircle size={20} style={{ color: theme.colors.dangerText }} />
                  )}
                  <span className="text-sm font-medium uppercase" style={{
                    color: company.status === 'running' ? theme.colors.primaryColor :
                           company.status === 'completed' ? theme.colors.successText :
                           company.status === 'error' ? theme.colors.dangerText :
                           theme.colors.textSecondary
                  }}>
                    {company.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs mb-1" style={{ color: theme.colors.textSecondary }}>Current Page</p>
                  <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{company.currentPage}</p>
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: theme.colors.textSecondary }}>Imported</p>
                  <p className="text-2xl font-bold" style={{ color: theme.colors.successText }}>{company.imported.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: theme.colors.textSecondary }}>Failed</p>
                  <p className="text-2xl font-bold" style={{ color: theme.colors.dangerText }}>{company.failed.toLocaleString()}</p>
                </div>
              </div>

              {company.error && (
                <div className="mt-4 p-3 rounded" style={{ 
                  backgroundColor: theme.colors.dangerBg,
                  color: theme.colors.dangerText 
                }}>
                  <p className="text-sm">{company.error}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Control Buttons */}
        <div className="flex gap-4">
          {!isImporting ? (
            <button
              onClick={startImport}
              className="flex-1 px-6 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#10b981', color: '#ffffff' }}
            >
              <Play size={20} />
              Start Import
            </button>
          ) : (
            <button
              onClick={pauseImport}
              className="flex-1 px-6 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#f59e0b', color: '#ffffff' }}
            >
              <Pause size={20} />
              Pause Import
            </button>
          )}
          
          <button
            onClick={resetImport}
            disabled={isImporting}
            className="flex-1 px-6 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: theme.colors.bgHover, color: theme.colors.textPrimary }}
          >
            <RotateCcw size={20} />
            Reset
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: theme.colors.bgHover }}>
          <p className="text-sm font-semibold mb-2" style={{ color: theme.colors.textPrimary }}>
            How it works:
          </p>
          <ul className="text-sm space-y-1" style={{ color: theme.colors.textSecondary }}>
            <li>• Imports 100 customers at a time from SuccessWare</li>
            <li>• Processes all three companies sequentially</li>
            <li>• You can pause and resume at any time</li>
            <li>• Data is automatically deduplicated</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SuccessWareFullImportPage;
